<script lang="ts" setup>
import { computed, ref, watch } from '#imports';
import { useService } from '@kaokei/use-vue-service';
import { useActiveElement, useMagicKeys } from '@vueuse/core';
import { ContextService } from '../Context.service';
import AudioRecorder from '../audio-recorder';
import { ImService } from '../im.service';
import ImSenderOffline from './ImSenderOffline.vue';
import AudioAction from './action/AudioAction.vue';
import AudioActionBtn from './action/AudioActionBtn.vue';
import CodeActionBtn from './action/CodeActionBtn.vue';
import EmojiAction from './action/EmojiAction.vue';
import EmojiActionBtn from './action/EmojiActionBtn.vue';
import ImageAction from './action/ImageAction.vue';
import ImageActionBtn from './action/ImageActionBtn.vue';
import ScreenshotAction from './action/ScreenshotAction.vue';
import ScreenshotActionBtn from './action/ScreenshotActionBtn.vue';
import SendActionBtn from './action/SendActionBtn.vue';

const service = useService(ImService);
const contextService = useService(ContextService);
const { enter, current } = useMagicKeys();
const activeElement = useActiveElement();
const textAreaRef = ref<HTMLTextAreaElement>();
const isTextPopulated = computed(() => !!service.input.trim());
const audioRecorder = new AudioRecorder();

const LOGO_URL =
  'https://img.hetao101.com/htoj/image/htxas/image/hetao-logo%402x.png';

function handleSendTxtMsg() {
  if (!isTextPopulated.value) {
    return;
  }
  service.messageService.sendTxtMsg(service.input);
  service.input = '';
}
function handleSendImgMsgByFile(file: File) {
  service.messageService.sendImgMsgByFile(file);
}
function handleSendCodeMsg({ title, url }: { title: string; url: string }) {
  service.messageService.sendCodeMsg(LOGO_URL, title, url);
}
function handleSelectEmoji(char: string) {
  service.input += char;
  textAreaRef.value?.focus();
}
watch(enter, (isPressed) => {
  if (
    !isPressed ||
    !activeElement.value ||
    activeElement.value?.id !== 'j_im_textarea'
  ) {
    return;
  }
  if (
    ['shift', 'alt', 'ctrl', 'meta', 'control'].some((key) => current.has(key))
  ) {
    service.input += '\n';
  } else {
    console.log('handleSendTxtMsg');
    handleSendTxtMsg();
  }
});
</script>

<template>
  <div
    class="relative h-[180px] flex-none border-t border-colorFillSecondary bg-white"
  >
    <div class="h-full pb-[18px]">
      <div class="flex gap-5 px-5 pt-3">
        <EmojiActionBtn />
        <ScreenshotActionBtn :send-image="handleSendImgMsgByFile"/>
        <CodeActionBtn
          :send-code="handleSendCodeMsg"
          :disabled="contextService.pageType !== 'PROBLEM_DETAIL#OJ'"
        />
        <ImageActionBtn :send-image="handleSendImgMsgByFile" />
        <div class="ml-auto flex items-center">
          <AudioActionBtn />
        </div>
      </div>
      <div class="flex h-[144px] flex-col pb-[66px] pt-4">
        <a-textarea
          id="j_im_textarea"
          ref="textAreaRef"
          v-model:value="service.input"
          :maxlength="500"
          :rows="5"
          :bordered="false"
          placeholder="练习遇到了问题吗？ 发个消息给老师吧～"
          class="hetao-scrollbar flex-1 !resize-none !px-6 !py-0 placeholder:!text-font4"
          @keydown.enter.prevent
        />
      </div>
      <ImSenderOffline v-if="!service.peerOnline" />
      <SendActionBtn :disbled="!isTextPopulated" @click="handleSendTxtMsg" />
    </div>

    <AudioAction :audio-recorder="audioRecorder" />
    <EmojiAction :select-emoji="handleSelectEmoji" />
    <ImageAction />
    <ScreenshotAction />
  </div>
</template>
<style scoped>
.btn-send:disabled {
  @apply !bg-colorTextQuaternary;
}
</style>
